import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import CategoryFilter from "./components/CategoryFilter";
import ArticleGrid from "./components/ArticleGrid";
import { useArticles } from "../../hooks/useArticles";
import { AdCintillo, AdSidebar } from "../../components/feature/SponsorAds";
import NewsletterPopup from "../../components/feature/NewsletterPopup";
import { useCategories } from "../../hooks/useCategories";
import { toNewsItems } from "../../lib/articleAdapter";
import { matchesCategory } from "../../lib/categoryAliases";
import { displayViewsLabel } from "../../lib/displayViews";
import { normalizeCategory } from "../../lib/categoryAliases";
import SEO from "@/components/SEO";

export default function Actualidad() {
	const [searchParams, setSearchParams] = useSearchParams();

	// Inicializar desde URL — sin categoría = mostrar todas las secciones
	const [activeCategory, setActiveCategory] = useState(() => {
		const cat = searchParams.get("categoria");
		return cat ? normalizeCategory(cat) : "";
	});
	const [search, setSearch] = useState(() => searchParams.get("search") || "");

	const { categories: dbCategories, standardCategories } = useCategories();

	// Single source of truth for articles
	const { articles, loading } = useArticles();
	const allItems = useMemo(() => toNewsItems(articles), [articles]);

	// Sync solo cuando el URL cambia externamente (botón atrás, links directos)
	useEffect(() => {
		const cat = searchParams.get("categoria");
		const q = searchParams.get("search");
		setActiveCategory((prev) => {
			const next = cat ? normalizeCategory(cat) : "";
			return prev === next ? prev : next;
		});
		setSearch((prev) => {
			const next = q || "";
			return prev === next ? prev : next;
		});
	}, [searchParams]);

	const searchKey = searchParams.toString();
	useEffect(() => {
		window.scrollTo({ top: 0, left: 0, behavior: "instant" });
	}, [searchKey]);

	// Calcular categorías que realmente tienen artículos (para el filtro)
	const categoriesWithArticles = useMemo(() => {
		if (loading) return [];
		const catsWithContent = new Set<string>();
		allItems.forEach((item) => {
			const cat = item.category || "";
			if (cat && cat.toLowerCase() !== "actualidad") {
				catsWithContent.add(normalizeCategory(cat));
			}
			if (Array.isArray(item.tags)) {
				item.tags.forEach((t: string) => {
					if (t && t.toLowerCase() !== "actualidad") {
						catsWithContent.add(normalizeCategory(t));
					}
				});
			}
		});
		const sections = standardCategories.filter((cat) =>
			catsWithContent.has(normalizeCategory(cat))
		);
		return ["Todas", ...sections];
	}, [allItems, standardCategories, loading]);

	const handleCategoryChange = (cat: string) => {
		if (cat === "Todas") {
			setActiveCategory("");
			setSearchParams({});
		} else {
			setActiveCategory(cat);
			setSearchParams({ categoria: cat });
		}
	};

	// Sidebar trending: filtrar por categoría activa y ordenar por views
	const trendingItems = useMemo(() => {
		const base = !activeCategory
			? allItems
			: allItems.filter((a) =>
					matchesCategory(
						a.category || "",
						Array.isArray(a.tags) ? a.tags : [],
						activeCategory,
					),
				);
		return [...base]
			.sort((a, b) => (b.views || 0) - (a.views || 0))
			.slice(0, 4);
	}, [allItems, activeCategory]);

	// Contador de artículos por categoría activa
	const categoryCount = useMemo(() => {
		if (!activeCategory) return allItems.length;
		return allItems.filter((a) =>
			matchesCategory(
				a.category || "",
				Array.isArray(a.tags) ? a.tags : [],
				activeCategory,
			),
		).length;
	}, [allItems, activeCategory]);

	// Títulos dinámicos con fallback
	const categoryTitles = useMemo(() => {
		const base: Record<string, { title: string; subtitle: string }> = {
			"": {
				title: "ACTUALIDAD AERONÁUTICA",
				subtitle:
					"Todas las noticias del sector aéreo organizadas por sección — Aerolíneas, Aeropuertos, Turismo, Estadísticas y más",
			},
		};
		// Subtítulos por defecto para secciones comunes
		const defaultSubtitles: Record<string, string> = {
			Nacional: "Actualidad aeronáutica de República Dominicana: aerolíneas, aeropuertos y operaciones locales",
			Internacional: "Lo más relevante de la aviación global y su impacto en el Caribe",
			Educación: "Formación, capacitación y desarrollo profesional en el sector aeronáutico dominicano",
			"Carga Aérea y Logística": "Operaciones de carga, logística aérea, exportaciones por vía aérea y terminal de carga",
			Entrevistas: "Conversaciones con líderes del sector aeronáutico dominicano y regional",
			Tendencias: "Innovación, tecnología, aviones del futuro y proyecciones del sector",
			Aerolíneas: "Nuevas rutas, flotas, alianzas y expansión de aerolíneas en el Caribe",
			Aeropuertos: "Infraestructura, modernización, certificaciones y tráfico aéreo",
			Sostenibilidad: "Combustibles verdes, aviación eléctrica y compromisos de carbono neutro",
			Turismo: "Conectividad turística, rutas charter, cruceros aéreos y viajes premium",
			Estadísticas: "Datos, cifras y análisis del tráfico aéreo en República Dominicana y el Caribe",
		};
		standardCategories.forEach((cat) => {
			if (!base[cat]) {
				base[cat] = {
					title: cat.toUpperCase(),
					subtitle:
						defaultSubtitles[cat] ||
						`Noticias y actualidad sobre ${cat.toLowerCase()} en el sector aeronáutico dominicano y el Caribe`,
				};
			}
		});
		return base;
	}, [standardCategories]);

	const currentCategoryMeta =
		categoryTitles[activeCategory] || categoryTitles[""];

	return (
		<main
			className="min-h-screen bg-[#F4F7FB]"
			style={{ fontFamily: "'Inter', sans-serif" }}
		>
			<SEO
				jsonLdSchemas={[
					{
						"@type": "WebPage",
						name: "Actualidad Aeronáutica",
						description: "Las últimas noticias del sector aéreo en República Dominicana y el Caribe.",
						url: 'https://aviacioninsightrd.com/actualidad',
						mainEntity: {
							"@type": "ItemList",
							itemListElement: [],
						},
					},
				]}
			/>
			<Navbar />

			{/* Page hero */}
			<section className="bg-[#0B1F3A] pt-32 pb-12">
				<div className="max-w-screen-xl mx-auto px-4 md:px-6">
					<div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
						<div>
							<div className="flex items-center gap-2 mb-3">
								<Link
									to="/"
									className="text-blue-400 text-sm font-inter hover:text-blue-300 cursor-pointer"
								>
									Inicio
								</Link>
								<i className="ri-arrow-right-s-line text-blue-600" />
								<Link
									to="/actualidad"
									className="text-blue-400 text-sm font-inter hover:text-blue-300 cursor-pointer"
								>
									Actualidad
								</Link>
								{activeCategory && (
									<>
										<i className="ri-arrow-right-s-line text-blue-600" />
										<span className="text-slate-400 text-sm font-inter">
											{activeCategory}
										</span>
									</>
								)}
							</div>
							<h1
								className="text-white font-bold"
								style={{
									fontFamily: "'Barlow Condensed', sans-serif",
									fontSize: "clamp(2rem, 5vw, 3rem)",
								}}
							>
								{currentCategoryMeta.title}
							</h1>
							<p className="text-blue-200/70 font-inter text-sm mt-2">
								{currentCategoryMeta.subtitle}
							</p>
						</div>
						<div className="flex gap-3 w-full sm:w-auto">
							<div className="bg-blue-900/40 rounded-xl px-4 py-3 text-center flex-1 sm:flex-initial">
								<p
									className="text-white font-bold text-xl"
									style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
								>
									{loading ? "..." : categoryCount}
								</p>
								<p className="text-blue-300 text-xs font-inter">
									{!activeCategory
										? "Artículos totales"
										: `En ${activeCategory}`}
								</p>
							</div>
							<div className="bg-blue-900/40 rounded-xl px-4 py-3 text-center flex-1 sm:flex-initial">
								<p
									className="text-white font-bold text-xl"
									style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
								>
									{standardCategories.length}
								</p>
								<p className="text-blue-300 text-xs font-inter">Secciones</p>
							</div>
						</div>
					</div>
				</div>
			</section>

			<AdCintillo placement="Actualidad" className="bg-[#F4F7FB]" />

			{/* Category filter */}
			<CategoryFilter
				active={activeCategory}
				onChange={handleCategoryChange}
				search={search}
				onSearch={setSearch}
				categories={categoriesWithArticles}
				loading={loading}
			/>

			{/* Main content */}
			<div className="max-w-screen-xl mx-auto px-4 md:px-6 py-10">
				<div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
					{/* Article grid */}
					<div className="lg:col-span-3">
						<ArticleGrid
							activeCategory={activeCategory}
							search={search}
							articles={articles}
							loading={loading}
							categories={dbCategories}
						/>
					</div>

					{/* Sidebar */}
					<aside className="space-y-8">
						{/* Trending */}
						<div className="bg-white rounded-xl p-5 border border-slate-100">
							<h3
								className="text-[#0B1F3A] font-bold mb-4 flex items-center gap-2"
								style={{
									fontFamily: "'Barlow Condensed', sans-serif",
									fontSize: "1.1rem",
								}}
							>
								<i className="ri-fire-line text-orange-500" />
								{!activeCategory
									? "MÁS LEÍDAS"
									: `EN ${activeCategory.toUpperCase()}`}
							</h3>
							{loading ? (
								<div className="space-y-3">
									{[1, 2, 3, 4].map((i) => (
										<div
											key={i}
											className="h-12 bg-slate-100 rounded-lg animate-pulse"
										/>
										))}
								</div>
							) : (
								<div className="space-y-4">
									{trendingItems.length > 0 ? (
										 trendingItems.map((item, i) => (
											<Link
												key={item.id}
												to={`/articulo/${item.slug}`}
												className="flex gap-3 group cursor-pointer"
											>
												<span className="flex-shrink-0 w-7 h-7 flex items-center justify-center bg-slate-100 text-slate-600 rounded-md text-xs font-bold font-inter group-hover:bg-slate-200 transition-colors">
													{i + 1}
												</span>
												<div>
													<p className="text-[#0B1F3A] text-sm font-inter font-medium leading-snug line-clamp-2 group-hover:text-slate-600 transition-colors">
														{item.title}
													</p>
													<p className="text-slate-400 text-xs font-inter mt-1">
														{item.date} · {displayViewsLabel(item.views || 0, item.slug)} lecturas
													</p>
												</div>
											</Link>
										))
										) : (
											<p className="text-slate-400 text-sm text-center py-4">
												No hay artículos en esta categoría.
											</p>
										)}
								</div>
							)}
						</div>

						{/* Publicidad: ocupa el lugar de la antigua caja de secciones (ya están en la barra de arriba).
						    El newsletter ahora sale como pop-up al entrar (NewsletterPopup). */}
						<AdSidebar
							placement="Actualidad"
							placeholders={[
								{ title: "¿Tu empresa aquí?", desc: "Llega a miles de lectores del sector aeronáutico" },
								{ title: "Turismo & Viajes", desc: "Conecta con viajeros del Caribe dominicano" },
							]}
						/>
					</aside>
				</div>
			</div>

			<NewsletterPopup />
			<Footer />
		</main>
	);
}